import { useQuery } from '@tanstack/react-query'
import {
  fetchCommissionEligibility,
  fetchCommissionEventBets,
  unavailableCommissionEligibility,
  type CommissionEligibilityQuery,
  type CommissionEligibilityResult,
} from '@/api/map/commissionEligibility'
import { keys } from '@/api/hooks/shared/keys'

/**
 * Lab `commission-report` list — CM-API `bqEligibleUsers` / `bqIneligibleUsers`.
 */
async function load(query: CommissionEligibilityQuery): Promise<CommissionEligibilityResult> {
  if (!query.from || !query.to) {
    return unavailableCommissionEligibility(query, 'Choose a week to search.')
  }
  try {
    return await fetchCommissionEligibility(query)
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    return unavailableCommissionEligibility(query, message || 'Commission eligibility failed.')
  }
}

export function useCommissionEligibility(query: CommissionEligibilityQuery, enabled = true) {
  return useQuery({
    queryKey: keys.commissionEligibility(query),
    queryFn: () => load(query),
    enabled: enabled && Boolean(query.from && query.to),
    placeholderData: (prev) => prev,
  })
}

/** Lab events drawer — CM-API `bqGetEventWiseBetCount`. */
export function useCommissionEventBets(
  args: {
    from: string
    to: string
    playerId: string
    isEligible: boolean
  },
  enabled = true,
) {
  return useQuery({
    queryKey: keys.commissionEventBets(args),
    queryFn: async () => {
      try {
        return await fetchCommissionEventBets({
          from: args.from,
          to: args.to,
          userIds: [args.playerId],
          isEligible: args.isEligible,
        })
      } catch {
        return []
      }
    },
    enabled: enabled && Boolean(args.from && args.to && args.playerId),
  })
}
